/* GB — Desktop hover menus — customization of Dawn header behavior.
   Foundation: Dawn's details-disclosure.js (header-menu / details-disclosure).
   Opens mega menus and dropdowns on pointer hover at desktop widths instead
   of requiring a click. Applies only on hover-capable devices at 990px+;
   touch, keyboard, and mobile keep stock Dawn behavior untouched.

   Reuses: Dawn's <details> open attribute (the native toggle event still
   fires, so HeaderMenu.onToggle animations and sticky preventHide run
   exactly as on click), DetailsDisclosure.close() (keeps aria-expanded in
   sync), stock focusout-to-close for keyboard users.
   New code: this file only — mouseenter/mouseleave listeners with a short
   close delay so the pointer can cross the gap between link and panel. */

(() => {
  const HOVER_MQ = window.matchMedia('(min-width: 990px) and (hover: hover)');
  const CLOSE_DELAY_MS = 150;

  const init = () => {
    document.querySelectorAll('.header__inline-menu header-menu, .header__inline-menu details-disclosure').forEach((disclosure) => {
      const details = disclosure.querySelector('details');
      const summary = details?.querySelector('summary');
      if (!details || !summary) return;

      let closeTimer;

      const close = () => {
        if (typeof disclosure.close === 'function') {
          disclosure.close();
        } else {
          details.removeAttribute('open');
          summary.setAttribute('aria-expanded', 'false');
        }
      };

      const open = () => {
        clearTimeout(closeTimer);
        // Only one top-level menu open at a time.
        document.querySelectorAll('.header__inline-menu details[open]').forEach((other) => {
          if (other !== details && !other.contains(details) && !details.contains(other)) {
            other.closest('header-menu, details-disclosure')?.close?.();
          }
        });
        if (!details.hasAttribute('open')) {
          details.setAttribute('open', '');
          summary.setAttribute('aria-expanded', 'true');
        }
      };

      disclosure.addEventListener('mouseenter', () => {
        if (HOVER_MQ.matches) open();
      });

      disclosure.addEventListener('mouseleave', () => {
        if (!HOVER_MQ.matches) return;
        clearTimeout(closeTimer);
        closeTimer = setTimeout(() => {
          // Don't close under a keyboard user who focused into the menu.
          if (!disclosure.contains(document.activeElement)) close();
        }, CLOSE_DELAY_MS);
      });

      /* GB — click navigates on hover devices: hover already opens the
         menu, so the top-level item acts as a plain link to its nav URL
         (data-gb-url, emitted by the menu snippets). Guards keep every
         other behavior stock: touch devices (no hover) keep click-to-
         toggle so the mega menu stays reachable, and keyboard activation
         (Enter/Space fires click with event.detail === 0) keeps toggling
         for accessibility. */
      summary.addEventListener('click', (event) => {
        if (!HOVER_MQ.matches) return;
        if (event.detail === 0) return;
        const url = summary.getAttribute('data-gb-url');
        if (!url) return;
        event.preventDefault();
        window.location.href = url;
      });
    });
  };

  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', init, { once: true }) : init();
})();

/* GB — Header links fall back to the hamburger when they would wrap (Oct 2026).
   Pairs with the two "Hamburger" blocks at the end of base.css.
   From 990 to 1199px the hamburger always shows (CSS only). From 1200px
   the link bar shows, unless its links need more room than the header's
   left column has, in any language and for whatever top-level links the
   menu has. Then this adds .gb-nav-collapsed to <html> and base.css shows
   the hamburger and drawer instead, exactly as between 990 and 1199px.
   There is never a second row of links.

   How it decides: the room the bar has (its own width; it stays in the
   layout, invisible, while collapsed, so this number never depends on the
   state) against the links on one row (the list measured at max-content
   for one synchronous read, never painted), with 2px to spare. Both are
   read the same way in both states, so the result cannot flip-flop.
   Re-checked whenever the header changes size (resize, rotation, the logo
   shrink), when the web fonts finish loading, and when the theme editor
   reloads the header.
   Only for the centred-logo header this theme uses (.header--middle-center);
   any other layout keeps stock behaviour. If this does not run, the bar
   simply shows at 1200px+, as before. */
(() => {
  if (!window.ResizeObserver || !window.matchMedia) return;

  const WIDE = window.matchMedia('(min-width: 1200px)');
  const root = document.documentElement;
  const SAFETY_PX = 2;
  let observer;
  let update = () => {};

  const setup = () => {
    if (observer) observer.disconnect();
    root.classList.remove('gb-nav-collapsed');

    const header = document.querySelector('.header.header--middle-center');
    const nav = header?.querySelector('.header__inline-menu');
    const list = nav?.querySelector('.list-menu--inline');
    if (!header || !nav || !list) {
      update = () => {};
      return;
    }

    const linksFit = () => {
      const navStyle = getComputedStyle(nav);
      const available = nav.clientWidth - parseFloat(navStyle.paddingLeft) - parseFloat(navStyle.paddingRight);

      const width = list.style.width;
      list.style.width = 'max-content';
      const listStyle = getComputedStyle(list);
      const needed =
        list.getBoundingClientRect().width + parseFloat(listStyle.marginLeft) + parseFloat(listStyle.marginRight);
      list.style.width = width;

      return needed <= available - SAFETY_PX;
    };

    update = () => {
      const collapse = WIDE.matches && nav.clientWidth > 0 && !linksFit();
      if (collapse === root.classList.contains('gb-nav-collapsed')) return;

      if (collapse) {
        // Close any open mega menu or dropdown before the bar is hidden.
        nav.querySelectorAll('details[open]').forEach((details) => {
          const disclosure = details.closest('header-menu, details-disclosure');
          if (disclosure && typeof disclosure.close === 'function') {
            disclosure.close();
          } else {
            details.removeAttribute('open');
            details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
          }
        });
      }
      root.classList.toggle('gb-nav-collapsed', collapse);
    };

    observer = new ResizeObserver(() => update());
    observer.observe(header);
    update();
  };

  if (typeof WIDE.addEventListener === 'function') WIDE.addEventListener('change', () => update());
  document.fonts?.ready.then(() => update());
  document.addEventListener('shopify:section:load', setup);
  setup();
})();
